export type NewsSubCategory = 'Announcements' | 'In the news' | 'Awards';

export const VALID_NEWSROOM_TYPES = new Set<NewsSubCategory>(['Announcements', 'In the news', 'Awards']);

// Older Newsroom MDX posts have no explicit sub-category field in frontmatter
// — infer Announcements vs Awards from the title for those. "In the news"
// posts (third-party press coverage) always set newsroomType explicitly, so
// they never reach this inference fallback.
const AWARD_RE = /\bwins?\b|\bwinning\b|\bawards?\b|\bhonou?r(ed)?\b|best in klas|ranked no\.?\s*1|ranks no\.?\s*1|\btop\s*(vendor|performer)\b|scores?\s*\d/i;

export function newsSubCategory(title: string): NewsSubCategory {
  if (AWARD_RE.test(title)) return 'Awards';
  return 'Announcements';
}

/**
 * A post can belong to multiple /news tabs at once (e.g. both "Announcements"
 * and "In the news"). Frontmatter may still hold the old single-scalar shape
 * (`newsroomType: In the news`) from before multi-select existed, or the new
 * array shape (`newsroomType: [Announcements, In the news]`) — this accepts
 * either and always returns a de-duplicated array, falling back to a single
 * title-inferred value only when the field is missing/invalid entirely.
 */
export function resolveNewsroomTypes(raw: unknown, title: string): NewsSubCategory[] {
  const values = Array.isArray(raw) ? raw : [raw];
  const valid = [...new Set(values.filter((v): v is NewsSubCategory => VALID_NEWSROOM_TYPES.has(v as NewsSubCategory)))];
  if (valid.length) return valid;
  return [newsSubCategory(title || '')];
}

// Matches CATEGORY_TO_SLUG in app/news/client-page.tsx — kept here too since the
// article breadcrumb (client-page.tsx under app/blogs) needs the same mapping.
export function newsSubCategorySlug(category: NewsSubCategory): string {
  if (category === 'Awards') return 'awards';
  if (category === 'In the news') return 'in-the-news';
  return 'announcements';
}
